import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { Button } from "@/components/Button/Button";
import { useNotification } from "@/context/NotificationContext";
import { resumeCampaign } from "@/api/resumeCampaign/resumeCampaignApi";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { EarliestAllowedResumeDate } from "@/modules/stochastic/features/CampaignManagement/api/fetchCampaignDetails/types";

interface ResumeCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaignId: number;
  earliestAllowedResumeDate?: EarliestAllowedResumeDate;
  onSuccess?: () => void;
}

const ResumeCampaignModal: React.FC<ResumeCampaignModalProps> = ({
  isOpen,
  onClose,
  campaignId,
  earliestAllowedResumeDate,
  onSuccess,
}) => {
  const { showNotification } = useNotification();
  const [isResuming, setIsResuming] = useState(false);

  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;
  const [selectedWeek, setSelectedWeek] = useState<number>(1);
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);

  useEffect(() => {
    if (isOpen && earliestAllowedResumeDate) {
      setSelectedWeek(earliestAllowedResumeDate.week);
      setSelectedYear(earliestAllowedResumeDate.year);
    }
  }, [isOpen, earliestAllowedResumeDate]);

  const isWeekDisabled = (week: number, year: number): boolean => {
    if (!earliestAllowedResumeDate) return false;

    const earliestWeek = earliestAllowedResumeDate.week;
    const earliestYear = earliestAllowedResumeDate.year;

    if (year < earliestYear) return true;

    return year === earliestYear && week < earliestWeek;


  };

  const getEarliestAllowedWeekForYear = (year: number): number => {
    if (!earliestAllowedResumeDate) return 1;

    const earliestYear = earliestAllowedResumeDate.year;
    const earliestWeek = earliestAllowedResumeDate.week;

    if (year === earliestYear) return earliestWeek;

    if (year > earliestYear) return 1;

    return 1;
  };

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    const earliestWeekForYear = getEarliestAllowedWeekForYear(year);
    setSelectedWeek(earliestWeekForYear);
  };

  const handleResumeCampaignClick = async () => {
    setIsResuming(true);
    try {
      await resumeCampaign({
        campaignId,
        week: selectedWeek,
        year: selectedYear,
      });
      showNotification(
        "Campaign Resumed",
        `Your campaign has been queued for resuming at Week ${selectedWeek}, ${selectedYear}.`,
        "success",
      );
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      onClose();
      console.error("Failed to resume campaign:", error);
    } finally {
      setIsResuming(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={{
        content: {
          top: "50%",
          left: "50%",
          right: "auto",
          bottom: "auto",
          transform: "translate(-50%, -50%)",
          borderRadius: "8px",
          padding: "24px",
          width: "500px",
          background: "white",
          boxShadow: "0 10px 25px rgba(0,0,0,.3)",
        },
        overlay: {
          backgroundColor: "rgba(0,0,0,0.3)",
          zIndex: 9999,
        },
      }}
    >
      <h3 className="text-xl font-semibold text-gray-900">Resume Campaign</h3>
      <p className="mt-3 text-sm text-gray-600">
        Note: The campaign end date will automatically be extended by the number
        of weeks the campaign was paused.
      </p>

      <div className="mt-6 grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="resume-week">Week</Label>
          <Select
            onValueChange={(value) => setSelectedWeek(Number(value))}
            value={selectedWeek.toString()}
          >
            <SelectTrigger id="resume-week">
              <SelectValue placeholder="Select week" />
            </SelectTrigger>
            <SelectContent className="z-[10001]">
              {Array.from({ length: 52 }, (_, i) => i + 1).map((week) => {
                const disabled = isWeekDisabled(week, selectedYear);
                return (
                  <SelectItem
                    disabled={disabled}
                    key={week}
                    value={week.toString()}
                  >
                    Week {week}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="resume-year">Year</Label>
          <Select
            onValueChange={(value) => handleYearChange(Number(value))}
            value={selectedYear.toString()}
          >
            <SelectTrigger id="resume-year">
              <SelectValue placeholder="Select year" />
            </SelectTrigger>
            <SelectContent className="z-[10001]">
              <SelectItem value={currentYear.toString()}>
                {currentYear}
              </SelectItem>
              <SelectItem value={nextYear.toString()}>{nextYear}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <p className="mt-4 text-sm text-gray-600">
        Are you sure you want to resume this campaign on{" "}
        <span className="font-bold">
          Week {selectedWeek}, {selectedYear}
        </span>
        ?
      </p>

      <div className="mt-6 flex justify-end space-x-3">
        <Button onClick={onClose} variant="plain">
          Cancel
        </Button>
        <Button
          disabled={isResuming}
          onClick={handleResumeCampaignClick}
          variant="default"
        >
          {isResuming ? "Resuming..." : "Resume Campaign"}
        </Button>
      </div>
    </Modal>
  );
};

export default ResumeCampaignModal;
